import { useEffect, type RefObject } from 'react';
import { sensX } from '../i18n';

/**
 * Passer d'un sous-onglet a l'autre au doigt.
 *
 * Le titre reste cliquable ; le balayage n'est qu'un second chemin vers
 * la meme chose. C'est le geste que tout le monde essaie devant deux
 * onglets cote a cote, et ne rien faire donne l'ecran pour fige.
 *
 * Trois precautions, chacune payee par un geste qui se marchait dessus :
 *
 * - rien ne part de la bande gauche, reservee au retour en arriere
 *   (`useSwipeBack`) — trente pixels, la meme largeur ;
 * - le geste doit etre franchement horizontal, sinon c'est la liste qu'on
 *   fait defiler ;
 * - il ne se declenche qu'au lacher, jamais en cours de route : changer
 *   d'onglet sous le doigt rechargerait la page a chaque tremblement.
 */

/** La bande gauche appartient au retour en arriere. */
const BORD_PX = 30;
/** En deca, c'est une hesitation, pas un balayage. */
const COURSE_PX = 55;
/** Au-dela de cette pente, le doigt voulait faire defiler. */
const PENTE = 0.7;

export function useSwipeTabs<T extends string>(
  zone: RefObject<HTMLElement>,
  /** L'onglet courant. */
  value: T,
  /** Les onglets, dans l'ordre ou ils s'affichent. */
  options: readonly { value: T }[],
  onChange: (next: T) => void,
): void {
  useEffect(() => {
    /*
      Le geste se prend sur toute la page, pas sur le seul bloc qui porte
      la reference : une liste courte, ou vide, laisserait sinon les trois
      quarts de l'ecran sourds au balayage. La zone defilante de l'ecran
      couvre exactement ce qu'on voit.
    */
    const el = (zone.current?.closest('.scroll-area') as HTMLElement | null) ?? zone.current;
    if (!el) return;

    const rang = options.findIndex((option) => option.value === value);
    if (rang < 0) return;

    let depart: { x: number; y: number } | null = null;

    function onStart(event: TouchEvent) {
      const doigt = event.touches[0];
      depart = null;
      if (event.touches.length !== 1 || !doigt) return;
      // Le bord ou nait le geste de retour : a gauche, ou a droite en arabe.
      if ((sensX === 1 ? doigt.clientX : window.innerWidth - doigt.clientX) <= BORD_PX) return;
      depart = { x: doigt.clientX, y: doigt.clientY };
    }

    function onEnd(event: TouchEvent) {
      const de = depart;
      const doigt = event.changedTouches[0];
      depart = null;
      if (!de || !doigt) return;

      const dx = (doigt.clientX - de.x) * sensX;
      const dy = doigt.clientY - de.y;
      if (Math.abs(dx) < COURSE_PX) return;
      if (Math.abs(dy) > Math.abs(dx) * PENTE) return;

      // Contre le sens de lecture : on avance ; dans son sens : on recule.
      const vise = options[dx < 0 ? rang + 1 : rang - 1];
      if (!vise) return;
      onChange(vise.value);
    }

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [zone, value, options, onChange]);
}
